/**
 * Produktvideo-Tracking (PDP-Galerie) → GTM dataLayer → GA4
 *
 * Pusht für jedes Video in der Produktgalerie die GA4-Standard-Events
 *   video_start     – erster Play pro Video und Seitenaufruf
 *   video_progress  – einmalig beim Überschreiten von 10 / 25 / 50 / 75 %
 *   video_complete  – Video zu Ende geschaut
 * mit denselben Parametern wie GA4 "Enhanced Measurement – Video engagement"
 * (video_provider, video_title, video_url, video_duration, video_current_time,
 * video_percent) plus Produkt-Kontext (product_id, product_name,
 * product_handle, video_media_id, video_position).
 *
 * Unterstützt Shopify-gehostete Videos (<video>), YouTube (iframe, benötigt
 * enablejsapi=1 – siehe snippets/product-gallery-external-video.liquid) und
 * Vimeo (iframe, postMessage-API).
 *
 * Consent: Es wird nur in den dataLayer gepusht. Ob daraus ein Hit an GA4
 * wird, entscheidet der GA4-Tag in GTM über Consent Mode (analytics_storage).
 *
 * Doku inkl. GTM-/GA4-Konfiguration: docs/tracking-produktvideos.md
 */
(function () {
  'use strict';

  const MILESTONES = [10, 25, 50, 75];
  const GALLERY_SELECTOR = '.product-gallery';
  const SLIDE_SELECTOR =
    '.product-gallery-swiper .swiper-slide[data-media-type="video"], ' +
    '.product-gallery-swiper .swiper-slide[data-media-type="external_video"]';

  /** @type {Map<string, {started: boolean, completed: boolean, milestones: Set<number>}>} */
  const state = new Map();

  function getState(mediaId) {
    if (!state.has(mediaId)) {
      state.set(mediaId, {
        started: false,
        completed: false,
        milestones: new Set(),
      });
    }
    return state.get(mediaId);
  }

  function readProductContext(gallery) {
    return {
      product_id: gallery.dataset.productId || '',
      product_name: gallery.dataset.productTitle || '',
      product_handle: gallery.dataset.productHandle || '',
    };
  }

  function toSeconds(value) {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : 0;
  }

  function push(eventName, meta, info) {
    window.dataLayer = window.dataLayer || [];
    // Alle Keys immer explizit setzen, damit im dataLayer keine Werte
    // aus einem vorherigen Push "kleben" bleiben.
    window.dataLayer.push({
      event: eventName,
      video_provider: meta.provider,
      video_title: meta.title,
      video_url: meta.url,
      video_media_id: meta.mediaId,
      video_position: meta.position,
      video_duration: toSeconds(info.duration),
      video_current_time: toSeconds(info.currentTime),
      video_percent: info.percent,
      product_id: meta.product.product_id,
      product_name: meta.product.product_name,
      product_handle: meta.product.product_handle,
    });
  }

  /**
   * Zentrale Auswertung: bekommt den aktuellen Player-Zustand und
   * entscheidet, welche Events (noch) gefeuert werden müssen.
   */
  function handleProgress(meta, { currentTime, duration, playing, ended }) {
    const s = getState(meta.mediaId);

    if (playing && !s.started) {
      s.started = true;
      push('video_start', meta, { currentTime, duration, percent: 0 });
    }
    if (!s.started) return;

    if (duration > 0) {
      const pct = Math.min(100, Math.floor((currentTime / duration) * 100));
      MILESTONES.forEach(m => {
        if (pct >= m && !s.milestones.has(m)) {
          s.milestones.add(m);
          push('video_progress', meta, { currentTime, duration, percent: m });
        }
      });
    }

    if (ended && !s.completed) {
      s.completed = true;
      push('video_complete', meta, { currentTime: duration, duration, percent: 100 });
    }
  }

  function baseMeta(slide, product, provider, url) {
    return {
      provider,
      url,
      title: slide.dataset.videoTitle || product.product_name,
      mediaId: slide.dataset.mediaId || '',
      position: Number(slide.dataset.mediaPosition) || 0,
      product,
    };
  }

  /* ---------------------------------------------------------------------- */
  /* Shopify-gehostetes Video (<video>)                                      */
  /* ---------------------------------------------------------------------- */
  function bindNativeVideo(slide, video, product) {
    const meta = baseMeta(slide, product, 'shopify', '');

    const snapshot = ended => ({
      currentTime: video.currentTime,
      duration: video.duration,
      playing: !video.paused,
      ended,
    });

    const refreshUrl = () => {
      if (!meta.url) meta.url = video.currentSrc || '';
    };

    video.addEventListener('play', () => {
      refreshUrl();
      handleProgress(meta, snapshot(false));
    });
    video.addEventListener('timeupdate', () => {
      handleProgress(meta, snapshot(false));
    });
    video.addEventListener('ended', () => {
      handleProgress(meta, snapshot(true));
    });
  }

  /* ---------------------------------------------------------------------- */
  /* YouTube (iframe, postMessage – benötigt enablejsapi=1)                  */
  /* ---------------------------------------------------------------------- */
  const YT_ORIGIN = 'https://www.youtube.com';
  const youtubePlayers = new Map(); // id → { meta, iframe, info }

  function ytSend(iframe, payload) {
    try {
      iframe.contentWindow.postMessage(JSON.stringify(payload), YT_ORIGIN);
    } catch (e) {
      /* iframe noch nicht bereit */
    }
  }

  function bindYouTube(slide, iframe, product) {
    const id = 'weck-yt-' + (slide.dataset.mediaId || youtubePlayers.size);
    const meta = baseMeta(slide, product, 'youtube', iframe.src);
    youtubePlayers.set(id, { meta, iframe, info: {} });

    const subscribe = () => {
      ytSend(iframe, { event: 'listening', id, channel: 'widget' });
      ytSend(iframe, {
        event: 'command',
        func: 'addEventListener',
        args: ['onStateChange'],
        id,
        channel: 'widget',
      });
    };
    subscribe();
    iframe.addEventListener('load', subscribe);
  }

  function onYouTubeMessage(event) {
    if (event.origin !== YT_ORIGIN || typeof event.data !== 'string') return;
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (e) {
      return;
    }
    const player = youtubePlayers.get(data.id);
    if (!player) return;

    if (data.event === 'infoDelivery' && data.info) {
      Object.assign(player.info, data.info);
    } else if (data.event === 'onStateChange') {
      player.info.playerState = data.info;
    } else {
      return;
    }

    const info = player.info;
    // playerState: 0 ended, 1 playing, 2 paused, 3 buffering
    handleProgress(player.meta, {
      currentTime: info.currentTime || 0,
      duration: info.duration || 0,
      playing: info.playerState === 1,
      ended: info.playerState === 0,
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Vimeo (iframe, postMessage)                                             */
  /* ---------------------------------------------------------------------- */
  const VIMEO_ORIGIN = 'https://player.vimeo.com';
  const vimeoPlayers = []; // { meta, iframe }

  function vimeoSend(iframe, payload) {
    try {
      iframe.contentWindow.postMessage(JSON.stringify(payload), VIMEO_ORIGIN);
    } catch (e) {
      /* iframe noch nicht bereit */
    }
  }

  function bindVimeo(slide, iframe, product) {
    const meta = baseMeta(slide, product, 'vimeo', iframe.src);
    vimeoPlayers.push({ meta, iframe });

    const subscribe = () => {
      ['play', 'timeupdate', 'ended'].forEach(value => {
        vimeoSend(iframe, { method: 'addEventListener', value });
      });
    };
    subscribe();
    iframe.addEventListener('load', subscribe);
  }

  function onVimeoMessage(event) {
    if (event.origin !== VIMEO_ORIGIN) return;
    let data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return;
      }
    }
    if (!data || !data.event) return;

    const player = vimeoPlayers.find(p => p.iframe.contentWindow === event.source);
    if (!player) return;

    const d = data.data || {};
    const duration = d.duration || 0;
    const currentTime = d.seconds || 0;

    handleProgress(player.meta, {
      currentTime,
      duration,
      playing: data.event === 'play' || data.event === 'timeupdate',
      ended: data.event === 'ended',
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Init                                                                    */
  /* ---------------------------------------------------------------------- */
  function init() {
    const gallery = document.querySelector(GALLERY_SELECTOR);
    if (!gallery) return;
    const product = readProductContext(gallery);
    const slides = gallery.querySelectorAll(SLIDE_SELECTOR);
    if (!slides.length) return;

    let needsYouTube = false;
    let needsVimeo = false;

    slides.forEach(slide => {
      if (slide.dataset.mediaType === 'video') {
        const video = slide.querySelector('video');
        if (video) bindNativeVideo(slide, video, product);
        return;
      }
      const iframe = slide.querySelector('iframe');
      if (!iframe) return;
      const host = slide.dataset.videoHost || '';
      if (host === 'youtube' || /youtube(-nocookie)?\.com/.test(iframe.src)) {
        bindYouTube(slide, iframe, product);
        needsYouTube = true;
      } else if (host === 'vimeo' || /vimeo\.com/.test(iframe.src)) {
        bindVimeo(slide, iframe, product);
        needsVimeo = true;
      }
    });

    if (needsYouTube) window.addEventListener('message', onYouTubeMessage);
    if (needsVimeo) window.addEventListener('message', onVimeoMessage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
